import {
    Button,
    Card,
    CardActions,
    CardContent,
    colors,
    Grid,
    Modal,
    Switch,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {T, useT} from 'react-polyglot-hooks';
import validate from 'validate.js';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import {cleanCellphone, InternationalPhoneInput} from '../../../components/PhoneInput/PhoneInput';
import anyLoading from '../../../utils/anyLoading';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers';
import createUser, {CreateUserVars} from './createUser.api';
import updateSelf, {UpdateSelfVars} from './updateSelf.api';
import updateUser, {UpdateUserVars} from './updateUser.api';
import {getMe} from '../../../graphql/auth';
import {checkIsAdmin} from '../../../utils/checkIsAdmin';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        outline: 'none',
        boxShadow: theme.shadows[20],
        width: 700,
        maxHeight: '90%',
        overflowY: 'auto',
        maxWidth: '95%'
    },
    container: {
        marginTop: theme.spacing(3)
    },
    actions: {
        justifyContent: 'flex-end'
    },
    saveButton: {
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    }
}));

const UserEdit = (props: {
    className?: string,
    user?: any,
    self?: boolean,
    confirmEmail?: boolean,
    onClose: () => void,
    open: boolean
}) => {
    const {open, onClose, user, self, confirmEmail, className, ...rest} = props;
    const classes = useStyles();
    const [sendCreateUser, createUserData] = createUser();
    const [sendUpdateUser, updateUserData] = updateUser();
    const [sendUpdateSelf, updateUserSelf] = updateSelf();
    const loadingData = anyLoading(createUserData, updateUserData, updateUserSelf);
    const isNewUser = !user.id;
    const {enqueueSnackbar} = useSnackbar();
    const meData = getMe();
    const loggedInEmail = meData && meData.data && meData.data.me && meData.data.me.email;
    const isAdmin = checkIsAdmin();
    const isLoggedInUser = user && loggedInEmail && (user.email === loggedInEmail);

    const t = useT();

    const newUser = {
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        email_verified: false,
        admin_verified: false,
        email_opt_out: false,
        sms_opt_out: false,
        cellphone: '',
        cellphone_code: '+1',
    };
    const [formState, setFormState] = useState(isNewUser ? newUser : {
        ...user,
        password: '',
        password_confirmation: ''
    });
    const [errors, setErrors] = useState({
        isValid: false,
        errors: {
            first_name: false,
            last_name: false,
            email: false,
            password: false,
            password_confirmation: false,
            cellphone: false,
            cellphone_code: false,
        }
    });

    const validateSchema = {
        email: {
            presence: {allowEmpty: false, message: t('is required')},
            email: true,
            length: {
                maximum: 64
            }
        },
        first_name: {
            presence: {allowEmpty: false, message: t('is required')},
            length: {
                maximum: 64
            }
        },
        last_name: {
            presence: {allowEmpty: false, message: t('is required')},
            length: {
                maximum: 64
            }
        },
        cellphone: {
            phone: true,
        },
        password: {
            password: {allowBlank: true},
        },
        password_confirmation: {
            equality: 'password',
        },
    };

    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);


    useEffect(() => {
        validateSchema.password.password.allowBlank = !(isLoggedInUser && user.onetime_password);

        const newErrors = validate(formState, validateSchema);
        setErrors(state => ({
            ...state,
            isValid: !newErrors,
            errors: newErrors || {}
        }));
    }, [formState]);

    const onSaveClick = () => {
        if (isNewUser) {
            const sanitisedData: CreateUserVars = {
                data: {
                    first_name: formState.first_name,
                    last_name: formState.last_name,
                    email: formState.email,
                    password: formState.password === '' ? undefined : formState.password,
                    admin_verified: formState.admin_verified,
                    email_verified: formState.email_verified,
                    email_opt_out: formState.email_opt_out,
                    sms_opt_out: formState.sms_opt_out,
                    cellphone: cleanCellphone(formState.cellphone),
                    cellphone_code: formState.cellphone_code,
                    roles: {
                        connect: [3] //TODO: Currently hard coded as role id 3 as it is a normal user. Fix
                    }
                }
            };

            sendCreateUser({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar(t('User Created.'), {variant: 'success'});
                    onClose();
                })
                .catch(() => {
                    enqueueSnackbar(t('User Creation Failed.'), {variant: 'error'});
                });
        } else if (self) {
            const sanitisedData: UpdateSelfVars = {
                data: {
                    id: formState.id,
                    first_name: formState.first_name,
                    last_name: formState.last_name,
                    email: formState.email,
                    password: formState.password === '' ? undefined : formState.password,
                    email_opt_out: formState.email_opt_out,
                    sms_opt_out: formState.sms_opt_out,
                    cellphone: cleanCellphone(formState.cellphone),
                    cellphone_code: formState.cellphone_code,
                    email_verified: confirmEmail ? true : undefined,
                }
            };

            sendUpdateSelf({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar(t('Updated Self.'), {variant: 'success'});
                    onClose();
                })
                .catch(() => {
                    enqueueSnackbar(t('Update Self Failed.'), {variant: 'error'});
                });
        } else {
            const sanitisedData: UpdateUserVars = {
                data: {
                    id: formState.id,
                    first_name: formState.first_name,
                    last_name: formState.last_name,
                    email: formState.email,
                    password: formState.password === '' ? undefined : formState.password,
                    admin_verified: formState.admin_verified,
                    email_verified: formState.email_verified,
                    email_opt_out: formState.email_opt_out,
                    sms_opt_out: formState.sms_opt_out,
                    cellphone: cleanCellphone(formState.cellphone),
                    cellphone_code: formState.cellphone_code,
                }
            };

            sendUpdateUser({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar(t('User Updated.'), {variant: 'success'});
                    onClose();
                })
                .catch(() => {
                    enqueueSnackbar(t('User Update Failed.'), {variant: 'error'});
                });
        }
    };

    return (
        <Modal onClose={onClose} open={open}>
            <Card {...rest} className={clsx(classes.root, className)}>
                <LoadingOverlay loading={loadingData.loading}>
                    <form>
                        <CardContent>
                            <Typography align="center" gutterBottom variant="h3">
                                {isNewUser ? t('Create User') : self ? t('Edit Profile') : t('Edit User')}
                            </Typography>
                            {isLoggedInUser && user.onetime_password && (
                                <Typography align="center" color="error" gutterBottom variant="body1">You must update
                                    your
                                    password before using the site.</Typography>
                            )}
                            <Grid className={classes.container} container spacing={3}>
                                <Grid item md={6} xs={12}>
                                    <TextField
                                        {...hasError('email')}
                                        fullWidth
                                        label={t('Email address')}
                                        name="email"
                                        onChange={handleFieldChange}
                                        value={formState.email}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <InternationalPhoneInput
                                        {...hasError('cellphone')}
                                        fullWidth
                                        label={t('Cellphone')}
                                        name="cellphone"
                                        onChange={handleFieldChange}
                                        onCodeChange={handleFieldChange}
                                        value={formState.cellphone}
                                        codeValue={formState.cellphone_code}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <TextField
                                        {...hasError('first_name')}
                                        fullWidth
                                        label={t('First Name')}
                                        name="first_name"
                                        onChange={handleFieldChange}
                                        value={formState.first_name}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <TextField
                                        {...hasError('last_name')}
                                        fullWidth
                                        label={t('Last Name')}
                                        name="last_name"
                                        onChange={handleFieldChange}
                                        value={formState.last_name}
                                        variant="outlined"
                                    />
                                </Grid>

                                {!isNewUser && (
                                    <Grid item md={6} xs={12}>
                                        <TextField
                                            {...hasError('password')}
                                            fullWidth
                                            label={t('Change Password')}
                                            name="password"
                                            type="password"
                                            onChange={handleFieldChange}
                                            value={formState.password}
                                            variant="outlined"
                                        />
                                    </Grid>
                                )}

                                {!isNewUser && (
                                    <Grid item md={6} xs={12}>
                                        <TextField
                                            {...hasError('password_confirmation')}
                                            fullWidth
                                            label={t('Confirm Password')}
                                            name="password_confirmation"
                                            type="password"
                                            onChange={handleFieldChange}
                                            value={formState.password_confirmation}
                                            variant="outlined"
                                        />
                                    </Grid>
                                )}

                                {!self && (
                                    <React.Fragment>
                                        <Grid item xs={12}>
                                            <Typography variant="h5"><T phrase="Email Verified"/></Typography>
                                            <Typography variant="body2">
                                                <T phrase="Disabling this will automatically send the user a verification email"/>
                                            </Typography>
                                            <Switch
                                                checked={formState.email_verified}
                                                color="secondary"
                                                edge="start"
                                                name="email_verified"
                                                onChange={handleFieldChange}
                                                value={formState.email_verified}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography variant="h5"><T phrase="Admin Verified"/></Typography>
                                            <Typography variant="body2">
                                                <T phrase="Has this users details been verified by an admin?"/>
                                            </Typography>
                                            <Switch
                                                checked={formState.admin_verified}
                                                color="secondary"
                                                edge="start"
                                                name="admin_verified"
                                                onChange={handleFieldChange}
                                                value={formState.admin_verified}
                                            />
                                        </Grid>
                                    </React.Fragment>
                                )}

                                <Grid item xs={12}>
                                    <Typography variant="h5"><T phrase="Email opt out"/></Typography>
                                    <Typography variant="body2">
                                        <T phrase="Opt out of all non-mandatory email communications?"/>
                                    </Typography>
                                    <Switch
                                        checked={formState.email_opt_out}
                                        color="secondary"
                                        edge="start"
                                        name="email_opt_out"
                                        onChange={handleFieldChange}
                                        value={formState.email_opt_out}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h5"><T phrase="SMS opt out"/></Typography>
                                    <Typography variant="body2">
                                        <T phrase="Opt out of all non-mandatory sms communications?"/>
                                    </Typography>
                                    <Switch
                                        checked={formState.sms_opt_out}
                                        color="secondary"
                                        edge="start"
                                        name="sms_opt_out"
                                        onChange={handleFieldChange}
                                        value={formState.sms_opt_out}
                                    />
                                </Grid>

                            </Grid>
                        </CardContent>
                        <CardActions className={classes.actions}>
                            <Button onClick={onClose} variant="contained"><T phrase="Close"/></Button>
                            <Button className={classes.saveButton} onClick={onSaveClick} variant="contained"
                                    disabled={!errors.isValid}><T phrase="Save"/></Button>
                        </CardActions>
                    </form>
                </LoadingOverlay>
            </Card>
        </Modal>
    );
};

export default UserEdit;
